import $$ from 'dom7';
import Framework7 from 'framework7/framework7.esm.bundle.js';
import * as moment from 'moment';

// Import F7 Styles
import 'framework7/css/framework7.bundle.css';

// Import Icons and App Custom Styles
import '../css/icons.css';
import '../css/app.scss';
// Import Cordova APIs
import cordovaApp from './cordova-app.js';
// Import Routes
import routes from './routes.js';
import defaultSoothers from './default-soothers.js';

function ensureDefaultSoothers(user) {
  if (!user || typeof db === 'undefined') {
    return Promise.resolve();
  }

  const userId = user.uid;
  const settingsRef = db.collection('settings').doc(userId);
  let existingSettings = null;

  return settingsRef
    .get()
    .then(function(doc) {
      existingSettings = doc.exists ? doc.data() : null;
      if (existingSettings && existingSettings.defaultSoothersSeeded) {
        return null;
      }
      return db
        .collection('soothers')
        .where('userId', '==', userId)
        .limit(1)
        .get();
    })
    .then(function(snapshot) {
      if (!snapshot) {
        return;
      }

      const settingsPayload = existingSettings
        ? {defaultSoothersSeeded: true}
        : {
            programStartDate: moment().format('YYYY-MM-DD'),
            maintenanceMode: false,
            defaultSoothersSeeded: true,
          };

      if (!snapshot.empty) {
        return settingsRef.set(settingsPayload, {merge: true});
      }

      const batch = db.batch();
      Object.keys(defaultSoothers).forEach(function(type) {
        (defaultSoothers[type] || []).forEach(function(value, index) {
          const docRef = db.collection('soothers').doc();
          batch.set(docRef, {
            userId: userId,
            value: value,
            type: type,
            order: index,
          });
        });
      });

      return batch.commit().then(function() {
        return settingsRef.set(settingsPayload, {merge: true});
      });
    })
    .catch(function(error) {
      console.error('Error ensuring default soothers', error);
    });
}

var app = new Framework7({
  root: '#app', // App root element
  id: 'com.sobrietyhabits.app', // App bundle ID
  name: 'Mindfulness Journal', // App name
  theme: 'auto', // Automatic theme detection
  // App root data
  data: function() {
    return {
      newEntries: [],
      auth: {},
      pillars: [
        null,
        {
          title: 'I Am Ready to Change',
          text:
            'I am ready to change. I have lived the way I have lived for too long. When I began this journey long ago my choices were just that; choices. Now they have grown into an addiction that controls me to the point that I feel I have no control. This process has affected my thinking, my feelings, and my being. Those close to me whom I love the most have also been impacted to the point that the life we once had has almost been destroyed.\n\nBut now, I am ready to change and I commit to pay whatever price is necessary to accomplish this. I have already seen the power of a single choice in my addiction. Now, I employ that same power, except I employ it for the purpose of growth and positive change. I will begin the change now and I will heal.',
        },
        {
          title: 'My Brain Can Change',
          text:
            'My brain can change. My brain can be my best friend. It can also be my worst enemy. If I do positive things in my life it is because of the neural circuitry that exists in my brain. If I do negative things in my life it is because of the neural circuitry that exists in my brain. Although my brain does not exist in a vacuum, it does work systemically with my body, my spirit and my soul. With the addiction that I have allowed my brain to develop, my brain has taken control over me. Now I am taking the control back. I will form better and healthier neural pathways in my brain. A neural pathway is a network of neurons that are responsible for behavior throughout my brain and body. Now I take control to develop healthier pathways in my brain and body. As I learn more about my brain I will become more and more in control. My brain is changing even as I read this pillar of healing. My brain can change and I will heal.',
        },
        {
          title: 'I Am Not Ashamed',
          text:
            'I am not ashamed. Throughout my life, I have been faced with challenges that have left me feeling shameful. Some of these were my fault, and some were not. This has left me in the position that my life has been shame based. I can now begin to face the reality that shame has been a part of my life, because of my past, but I also know that I have total control over my future. I am not ashamed. I will treat each day as if it were the last one that I will live, and live it with no regrets. I know that the shame that occasionally threatens to sink me into the pits of despair, is a part of my old life, and does not control my future. I know that healthy shame or guilt can be a motivator for great things. Guilt has an important purpose in my life. It is a friend and a guide. I will use this companion as it was meant to be used, and I will not be ashamed. I am not ashamed and I will shout this fact from the rooftops! My recovery will never be complete until I deal with toxic shame, but I also must have guilt or healthy shame to push me towards greatness. I am not ashamed, and I am becoming healthy.',
        },
        {
          title: 'Empathy',
          text:
            'In the past my actions have not been empathic. I have hurt those I love and most of all I have hurt myself. From this moment on I commit to live, feel, and express empathy in all that I do. I will think of how my addictive and acting out behaviors have impacted those I love in the past. From this point on, as I am compelled to engage in any form of acting out I will stop and feel how this action would impact those around me and then I will act in an empathic manner. From this point on as I am compelled to engage in any form of acting out, I will identify how this will impact me and I will act in an empathic manner. I will also identify how my behaviors may impact those who do not know me. As I am empathic to others and myself my life will change.',
        },
        {
          title: 'Consistency',
          text:
            'Consistency is a friend of unhealthy behaviors. It can also be a friend of healthy behaviors. Through consistently engaging in unhealthy thinking and behaviors I have developed a pattern of acting out. This pattern has become a mechanism for coping, and by so coping, unhealthy patterns have come to control my life. Just as consistency has been a foe, it can also become a friend. From this point forward, I will act consistently with what I believe. I know that with each consistent choice, greater learning takes place and my behaviors will become closer to being part of me. As I consistently make healthy choices and avoid acting out processes I will move closer to freedom and emotional health. Healthy choices made consistently will facilitate change at the cellular level of my being. As these cellular changes take place, my entire physiology is changing and improving. I will act consistently and I will heal.',
        },
        {
          title: 'Frequency',
          text:
            'Good choices made frequently lead to life long, positive changes. From this point on, I will not only make wise choices but I will make them frequently. With each choice that I make, the choice becomes part of me. Therefore, I will choose to not add to the frequency of unhealthy choices anymore. I will choose to make wise and healthy choices on a frequent basis. This helps these decisions to become part of my very being. With each healthy choice made frequently, I will increase my freedom. The more often I choose healthy choices the stronger I become. I will remember that as I choose healthy actions frequently, my brain continues to change and I become freer and in reality, more healthy.',
        },
        {
          title: 'Duration',
          text:
            'I will lengthen the duration of my healthy choices. I will choose wise choices over an extended period of time. I have started to make healthy choices on a daily basis. I will continue to do this but I will extend the days into weeks and the weeks into months, the months into years, and the years into a lifetime. I alone am responsible for these choices being made. As I lengthen the duration of my choices my confidence will increase and I will heal. From now on I will no longer blame other people for my thoughts, my feelings, my actions, or my life. I can choose my future and I will do this by making healthy choices for the duration of my life.',
        },
        {
          title: 'Intensity',
          text:
            'Intensity has been one of my worst enemies. The more intense a stimuli is the greater impact it has upon my brain and my body. The negative things I have experienced and chosen to take in have been intense - hence, they have shaped my being. From this point forward, I will remember the principle of intensity and I will never willingly allow such an intense and negative experience to come into my life. For if I do allow such a thing, I am making a choice to stay sick. Healthy changes can be made without intensity so long as I practice and apply the principles of consistency, frequency and duration.',
        },
        {
          title: 'Reflection',
          text:
            'When I started my journey I committed to paying whatever price was necessary in order to get well. To this point, I have done that but I recognize this is only the beginning. I commit at this time to continue to pay whatever price is necessary and I will make that same commitment every day. As I continue to make that commitment I will continue to become healthier. Life is worth living and living means more than simply surviving. Living means growing and I have now started the process of growing. I will continue to pay the price of growing and make the decisions I need to make to keep myself safe.',
        },
        {
          title: 'Balance and Equilibrium',
          text:
            'My life is about balance and equilibrium. This life is a jumble of conflicting time constraints and priorities. I know in the past, I have been torn in all directions by the demands of others. But through my healing, I have learned that balance is essential to maintaining my sobriety. When I find myself being overwhelmed with life, when my finances are out of control, or I feel hopeless in my relationships; at those time when I feel the most down, I will think about balance (what parts of my life are over-emphasized and which have been neglected). In much the same way as a song has numerous different notes going all different directions; when they are balanced, they become a beautiful blend of melody and harmony. My life is a jumble of priorities that I must blend to make it productive and smooth. When balance is lost, I will take the time to focus and rebalance. When I apply the principals of balance to my life with consistency, frequency and duration and I do them with the urgency of getting the healing that I need, these principles bring me to my new equilibrium. My life is about balancing my new equilibrium, and I am happy.',
        },
        {
          title: 'Gratitude',
          text:
            'I have now tasted healing. I have tasted the beauty of being congruent. I have tasted the benefits of honesty. For all of this, I feel gratitude. I will now allow gratitude to be a guiding star in my life. My life is better today than it was several months ago when I began this journey and for this, I fell gratitude. I have learned a secret during this walk and the secret is this: As I feel and express gratitude for both the pain and the joy in my life I am able to stay grounded. As I stay grounded I think more clearly and as I think clearly I make rational choices. As I make rational choices I make the conscious choice to stay healthy. Hence, I will continue to show gratitude. I will find someone every day to express gratitude to. No matter how painful a certain day may be I will find something to express gratitude for even if the thing I express gratitude for is the ability to feel pain. For the same mechanism that allows me to feel pain also allows me to feel joy. I will feel and express gratitude and I will continue to stay healthy.',
        },
        {
          title: 'Maintenance',
          text:
            'I now know the importance of avoiding unhealthy stimuli and choices. I will avoid unhealthy choices. I will avoid unhealthy stimuli. My choices to not avoid these things in the past have contributed to my current place in life. From this point on, as I have the desire to act out I will remember what I really want. I will act accordingly and I will avoid all unhealthy stimuli. I will pay whatever physical, emotional, psychological, spiritual, and financial price is necessary to avoid these stimuli and get well. I will do this because I am making an investment in one of the most important creations in the universe - me. I would choose to walk a thousand miles many times over before I will allow unhealthy stimuli to come into my life again. I will avoid unhealthy stimuli and I will stay well.',
        },
      ],
    };
  },
  // App root methods
  methods: {},
  // App routes
  routes: routes,

  // Input settings
  input: {
    scrollIntoViewOnFocus: Framework7.device.cordova && !Framework7.device.electron,
    scrollIntoViewCentered: Framework7.device.cordova && !Framework7.device.electron,
  },
  // Cordova Statusbar settings
  statusbar: {
    overlay: (Framework7.device.cordova && Framework7.device.ios) || 'auto',
    iosOverlaysWebView: true,
    androidOverlaysWebView: false,
  },
  on: {
    init: function() {
      var f7 = this;
      if (f7.device.cordova) {
        // Init cordova APIs (see cordova-app.js)
        cordovaApp.init(f7);
      }

      let self = this;
      let promiseHelper;

      self.authPromise = new Promise((resolve, reject) => {
        promiseHelper = {
          resolve,
          reject,
        };
      });

      const processAuthState = function(user) {
        if (user) {
          const finalizeLogin = function() {
            if (self.loginScreenInstance) {
              self.loginScreenInstance.close(false);
            } else {
              self.loginScreen.close('#my-login-screen');
            }
            self.data.user = user;
            promiseHelper.resolve(user);
            self.emit('logged-in');
          };
          Promise.resolve(ensureDefaultSoothers(user))
            .then(finalizeLogin)
            .catch(function(error) {
              if (error) {
                console.error('Error seeding default soothers during login', error);
              }
              finalizeLogin();
            });
        } else {
          if (!self.loginScreenInstance) {
            self.loginScreenInstance = self.loginScreen.create({el: '.login-screen'});
          }
          self.loginScreenInstance.open(false);
        }
      };

      const handleAuthError = function(error) {
        promiseHelper.reject(error);
      };

      // First handle any redirect result, then set up the auth state listener
      firebase
        .auth()
        .getRedirectResult()
        .then(function() {
          firebase.auth().onAuthStateChanged(processAuthState, handleAuthError);
        })
        .catch(function(error) {
          console.error('Auth redirect error', error);
          firebase.auth().onAuthStateChanged(processAuthState, handleAuthError);
        });
    },
  },
});

window.moment = moment;
window.$$ = $$;
window.app = app;

// Register service worker for PWA support
if ('serviceWorker' in navigator && process.env.TARGET === 'web') {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/static/service-worker.js')
      .then((registration) => {
        console.log('[SW] Service Worker registered successfully:', registration.scope);

        // Check for updates periodically
        setInterval(() => {
          registration.update();
        }, 60000); // Check every minute
      })
      .catch((error) => {
        console.log('[SW] Service Worker registration failed:', error);
      });
  });

  // Handle install prompt
  let deferredPrompt;

  window.addEventListener('beforeinstallprompt', (e) => {
    // Prevent the mini-infobar from appearing on mobile
    e.preventDefault();
    // Stash the event so it can be triggered later
    deferredPrompt = e;
    console.log('[PWA] Install prompt available');

    // Optionally, show your own install button
    app.data.canInstall = true;
  });

  window.addEventListener('appinstalled', () => {
    console.log('[PWA] App installed successfully');
    app.data.canInstall = false;
    deferredPrompt = null;
  });

  // Function to show install prompt
  window.showInstallPrompt = () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === 'accepted') {
          console.log('[PWA] User accepted the install prompt');
        } else {
          console.log('[PWA] User dismissed the install prompt');
        }
        deferredPrompt = null;
      });
    }
  };
}
